<template>
  <div
    class="annotation-toolbar uk-flex uk-flex-between uk-flex-middle uk-flex-right uk-width-1-1"
    :class="{ 'uk-margin-small-bottom': vertical }"
  >
    <!-- Left section: Instruction icon and author badge -->
    <div v-if="!publicView" class="uk-flex uk-flex-middle">
      <!-- Instruction icon -->
      <div
        v-if="showInstructionIcon"
        class="annotation-instruction-icon uk-flex-center uk-flex uk-flex-middle uk-margin-small-right"
      >
        <img
          :src="`/icons/Inlinerules.svg`"
          style="width: 35px"
          :uk-tooltip="instructionTooltip"
          @click="onRepliesClick"
        />

        <button
          v-if="showTeamRepliesButton"
          class="uk-button uk-button-small uk-padding-remove-top uk-padding-remove-bottom uk-flex-center uk-flex uk-flex-middle uk-margin-small-right uk-border-pill uk-margin-small-left"
          :title="`Your team has included ${teamRepliesCount} annotations.`"
          @click="onRepliesClick"
        >
          <span>{{ teamRepliesCount }}</span>
          <span uk-icon="comment"></span>
          <span v-if="hasSelectedReply" uk-icon="check" class="uk-text-primary"></span>
        </button>
      </div>

      <!-- Author badge -->
      <div v-if="showAuthorBadge" class="uk-badge small-badge uk-light uk-background-secondary">
        {{ authorName }}
      </div>
    </div>

    <div class="invisible-divider"></div>

    <!-- Right section: Actions (cog menu and citations) -->
    <div v-if="showActions" class="uk-flex cog">
      <!-- Cog dropdown menu -->
      <button
        class="uk-flex-center uk-flex uk-flex-middle uk-dark custom-icon-padding btn-background custom-icon-height"
        type="button"
      >
        <span uk-icon="cog"></span>
      </button>
      <div uk-dropdown="mode: click" style="width: max-content">
        <ul class="uk-nav uk-dropdown-nav">
          <li v-if="showSwitchTypeOption">
            <a @click="onSwitchType">
              <img :src="`/icons/black/icon-black-citation.svg`" width="30" />
              <span>Make {{ switchTypeLabel }}</span>
            </a>
          </li>
          <li v-if="showThreadsView">
            <a @click="onShowThreadsView">
              <span
                uk-icon="icon: more-vertical; ratio: 1.2"
                class="uk-margin-small-right color-black"
              >
                <span
                  uk-icon="icon: expand; ratio: 1.2"
                  class="uk-margin-small-right uk-position-absolute color-black"
                ></span>
              </span>
              <span>Threads View</span>
            </a>
          </li>
          <li v-if="canDelete">
            <hr />
          </li>
          <li v-if="canDelete">
            <a @click="onDelete">
              <span
                uk-icon="icon: ban; ratio: 1.5"
                class="uk-margin-small-right"
                style="color: #800020"
              ></span>
              <span style="color: #9f1d35">{{ deleteLabel }}</span>
            </a>
          </li>
        </ul>
      </div>

      <!-- Citation controls -->
      <div
        v-if="showCitationControls"
        :class="{ 'custom-icon-padding': !hasCitations }"
        class="uk-flex uk-flex-column"
      >
        <div v-if="hasCitations" class="uk-flex uk-flex-column" @click="onToggleCitation">
          <img
            :src="`/icons/black/icon-black${citationOpen ? '-close' : '-open'}-citation.svg`"
            width="25"
            style="height: 24px"
            class="custom-icon-height-margin citation-icon"
            :class="{ 'open-citation-style': !citationOpen }"
          />
        </div>
        <a
          v-else
          @click="onAddCitation"
          :class="{ 'citations-dis': citationDisabled }"
          style="height: 28px; margin-top: -6px"
          uk-tooltip="Click to add citation"
        >
          <img
            :src="`/icons/black/icon-black-create-citation.svg`"
            width="25"
            style="opacity: 0.5; height: 24px"
            class="custom-icon-height-margin citation-icon"
          />
        </a>
      </div>
    </div>

    <!-- Published view citation toggle -->
    <div
      v-if="hasCitations && roundPublished && hover"
      class="default-citaiton-icon"
      @click="onToggleCitation"
      style="cursor: pointer"
    >
      <img
        :src="`/icons/black/icon-black${citationOpen ? '-close' : '-open'}-citation.svg`"
        class="citation-img"
        width="25"
        style="height: 24px"
        :class="{ 'open-citation-style': !citationOpen }"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

// Props
interface Props {
  publicView?: boolean;
  isInstruction?: boolean;
  isHostInstructionMode?: boolean;
  isTeamLeadOrHostMode?: boolean;
  showCounterpointSelect?: boolean;
  annotationType?: string;
  permissionLevel?: any;
  isCurrentAuthor?: boolean;
  isCurrentRound?: boolean;
  showApprovalButtons?: boolean;
  authorName?: string;
  teamRepliesCount?: number;
  hasSelectedReply?: boolean;
  canUpdateAndDelete?: boolean;
  roundPublished?: boolean;
  isTeamModeActive?: boolean;
  myLatestRoundNumber?: number;
  canDelete?: boolean;
  deleteLabel?: string;
  hasCitations?: boolean;
  citationOpen?: boolean;
  citationDisabled?: boolean;
  hover?: boolean;
  roundStatus?: any;
  annotationText?: string;
  parentCounterpointId?: string;
  currentTeamLatestRoundCounterpointsId?: string;
  isGroupRoundUser?: boolean;
  vertical?: boolean;
  isCohortTeam?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  publicView: false,
  isInstruction: false,
  isHostInstructionMode: false,
  isTeamLeadOrHostMode: false,
  showCounterpointSelect: false,
  isCurrentAuthor: false,
  isCurrentRound: false,
  showApprovalButtons: false,
  canUpdateAndDelete: true,
  roundPublished: false,
  isTeamModeActive: false,
  myLatestRoundNumber: 1,
  canDelete: false,
  hasCitations: false,
  citationOpen: false,
  citationDisabled: true,
  hover: false,
  isGroupRoundUser: false,
  vertical: false,
  isCohortTeam: false,
});

// Emits
const emit = defineEmits([
  'repliesClick',
  'switchType',
  'showThreadsView',
  'delete',
  'toggleCitation',
  'addCitation',
]);

// Computed
const showInstructionIcon = computed(() => {
  return (
    props.isInstruction &&
    (props.isHostInstructionMode ||
      props.parentCounterpointId === props.currentTeamLatestRoundCounterpointsId)
  );
});

const instructionTooltip = computed(() => {
  if (props.showCounterpointSelect) return props.annotationText;
  if (props.annotationType === 'hostInstruction') return 'Organization Host Instruction';
  if (props.permissionLevel === 'TEAM_LEAD') return 'Write instructions for you team.';
  return 'Write your annotations according to these instructions.';
});

const showTeamRepliesButton = computed(() => {
  return (
    props.isTeamLeadOrHostMode &&
    props.isInstruction &&
    props.teamRepliesCount > 0 &&
    !props.showCounterpointSelect
  );
});

const showAuthorBadge = computed(() => {
  return (
    (!props.isInstruction && !props.isCurrentAuthor && props.isCurrentRound) ||
    !props.isCurrentAuthor ||
    props.showApprovalButtons
  );
});

const showActions = computed(() => {
  const canShowInPublishedRound = props.isCohortTeam && props.isCurrentAuthor;

  return (
    (props.isCurrentAuthor ||
      props.annotationType === 'instruction' ||
      (props.roundStatus === 'FINALIZING' && props.isCurrentAuthor) ||
      props.isGroupRoundUser) &&
    props.canUpdateAndDelete &&
    (!props.roundPublished || canShowInPublishedRound)
  );
});

const showCitationControls = computed(() => {
  const canShowInPublishedRound = props.isCohortTeam && props.isCurrentAuthor;

  return !props.roundPublished || canShowInPublishedRound;
});

const showSwitchTypeOption = computed(() => {
  return props.isTeamModeActive && props.isCurrentAuthor && !props.showCounterpointSelect;
});

const switchTypeLabel = computed(() => {
  return props.annotationType === 'instruction' ? 'Annotation' : 'Inline Rule';
});

const showThreadsView = computed(() => {
  return props.myLatestRoundNumber > 3 && !props.vertical;
});

// Methods
const onRepliesClick = () => emit('repliesClick');
const onSwitchType = () => emit('switchType');
const onShowThreadsView = () => emit('showThreadsView');
const onDelete = () => emit('delete');
const onToggleCitation = () => emit('toggleCitation');
const onAddCitation = () => emit('addCitation');
</script>

<style scoped lang="scss">
.cog {
  opacity: 0;
}

.annotation:hover .cog {
  opacity: 1;
}

.custom-icon-padding {
  padding: 0 0 0 5px;
}

.btn-background {
  cursor: pointer;
  background: none;
  border: none;
}

.custom-icon-height {
  height: 25px;
}

.citations-dis {
  pointer-events: none;
}

.color-black {
  color: black;
}

.open-citation-style {
  margin-top: 2px;
}

.default-citaiton-icon {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.small-badge {
  font-size: $global-small-font-size !important;
}
</style>
